import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { PetChatPage, PetChatReplay, PetChatSummary } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";

/**
 * 「TA 的朋友们」聊天回放的取数（P2，6796 起草；路由与接线归 6c2b）。两条纯读路由：
 * GET /pets/{id}/pet-chats（新的在前，游标翻页）、GET /pets/{id}/pet-chats/{thread_id}（整场回放，没有游标）。
 * 查询键挂在 "social" 前缀下、按账号＋宠物分开（写法同 petFriends 的 petFriendsKey：设置页改动后按 ["social"] 失效时一起重读）。
 */
export const petChatsKey = (userId: string | null, petId: string | null) => ["social", "pet-chats", userId ?? "-", petId ?? "-"] as const;
export const petChatKey = (userId: string | null, petId: string | null, threadId: string | null) =>
  ["social", "pet-chat", userId ?? "-", petId ?? "-", threadId ?? "-"] as const;

/** 一页多少场：与后端默认同为 30 */
export const PET_CHAT_PAGE = 30;

/** 会话列表：往下翻取更早的（next_cursor 原样带回）。演示模式照问演示服务（答「演示里没有」），live 要有账号和宠物。 */
export function usePetChatList(petId: string | null) {
  const { pets } = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const query = useInfiniteQuery<PetChatPage, Error, { pages: PetChatPage[] }, ReturnType<typeof petChatsKey>, string | null>({
    queryKey: petChatsKey(userId, petId),
    queryFn: ({ pageParam, signal }) => pets.petChats(petId!, { cursor: pageParam, limit: PET_CHAT_PAGE }, signal),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(petId) && (env.dataMode === "fixture" || Boolean(userId)),
    retry: false,
  });
  const items: PetChatSummary[] = query.data?.pages.flatMap((page) => page.items) ?? [];
  return { query, items };
}

/** 一场聊天的回放：整场一次读完（契约没有游标）。 */
export function usePetChatReplay(petId: string | null, threadId: string | null) {
  const { pets } = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return useQuery<PetChatReplay>({
    queryKey: petChatKey(userId, petId, threadId),
    queryFn: ({ signal }) => pets.petChat(petId!, threadId!, signal),
    enabled: Boolean(petId && threadId) && (env.dataMode === "fixture" || Boolean(userId)),
    retry: false,
  });
}

/**
 * 时间按看的人的本地时间写：同一天只写几点（「14:05」）；同一年写「10月2日」（withClock 时带几点）；跨年写「2025年10月2日」。
 * 读不出的时刻返回 null（不编）。
 */
export function chatTimeText(iso: string | null | undefined, now: Date = new Date(), withClock = false): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const clock = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
  const sameDay = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  if (sameDay) return clock;
  const day = `${at.getMonth() + 1}月${at.getDate()}日`;
  const date = at.getFullYear() === now.getFullYear() ? day : `${at.getFullYear()}年${day}`;
  return withClock ? `${date} ${clock}` : date;
}
